import { Box, Stack, Typography } from "@mui/material";
import { Code } from "@pulse/pulse-rpcs/lib/types/code";
import { GridAnswerV1 } from "@pulse/pulse-rpcs/lib/types/gridAnswerV1";
import { MultiChoiceAnswerV1 } from "@pulse/pulse-rpcs/lib/types/multiChoiceAnswerV1";
import { OpenEndedAnswerV1 } from "@pulse/pulse-rpcs/lib/types/openEndedAnswerV1";
import { RankingAnswerV1 } from "@pulse/pulse-rpcs/lib/types/rankingAnswerV1";
import { SingleChoiceAnswerV1 } from "@pulse/pulse-rpcs/lib/types/singleChoiceAnswerV1";
import { ChildQuestionElementV1Enums } from "@pulse/pulse-rpcs/lib/types/childQuestionElementV1";
import {
  GroupAnswerElementV1,
  GroupAnswerElementV1Enums,
} from "@pulse/pulse-rpcs/lib/types/groupAnswerElementV1";
import { GroupAnswerV1 } from "@pulse/pulse-rpcs/lib/types/groupAnswerV1";
import { GroupWithChildQuestionsV1 } from "@pulse/pulse-rpcs/lib/types/groupWithChildQuestionsV1";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  EMPTY_JSON,
  createGridQuestionV1Model,
  createOpenEndedQuestionV1Model,
  isBrowserLightMode,
} from "@pulse/shared-components";
import {
  Button,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChatMultiSelectAnswerList,
  ChatMultiSelectQuestionBody,
} from "./ChatMultiSelectQuestionBody";
import {
  ChatOpenEndedAnswer,
  ChatOpenEndedQuestionBody,
} from "./ChatOpenEndedQuestionBody";
import {
  ChatRankingAnswerList,
  ChatRankingQuestionBody,
  createChatRankingQuestionModel,
} from "./ChatRankingQuestionBody";
import {
  ChatSingleSelectAnswerText,
  ChatSingleSelectQuestionBody,
} from "./ChatSingleSelectQuestionBody";
import { ChatItemContainer } from "./ChatItemContainer";
import { revealNewChatStep } from "./ChatScroll";
import { validateGridAnswer } from "../../../utils/validateGridAnswer";
import {
  ChatGridQuestionActive,
  ChatGridQuestionSummary,
} from "./ChatGridQuestion";

export interface ChatGroupQuestionLabels {
  cancelEditAnswerText: string;
  nextQuestionText: string;
  sendResponseText: string;
  previousText: string;
  skippedAnswerText: string;
  showMoreText: string;
  showLessText: string;
  dropdownPlaceholder: string;
  multiSelectMaxSelectionText: string;
  rankingAllOptionsMandatoryText: string;
  editAnswerText: string;
}

export interface ChatGroupQuestionActiveProps {
  questionDetailsJson: string;
  completedAnswerJson?: string | null;
  parentIsMandatory: boolean;
  initialChildQuestionCode?: string;
  initialGridRowCode?: string;
  labels: ChatGroupQuestionLabels;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  areActionsDisabled?: boolean;
  isEditing?: boolean;
  onCancelEdit?: () => void;
  onSubmit: (answerJson: string) => void;
}

export interface ChatGroupQuestionCompletedProps {
  questionDetailsJson: string;
  completedAnswerJson: string;
  labels: Pick<
    ChatGroupQuestionLabels,
    "skippedAnswerText" | "showMoreText" | "showLessText" | "editAnswerText"
  >;
  areActionsDisabled?: boolean;
  showEditActions?: boolean;
  onEditChild: (childQuestionCode: string, gridRowCode?: string) => void;
}

type ChildKind = "single" | "multi" | "open" | "ranking" | "grid" | "message";

const ignoreRankingReset = (): void => undefined;

interface Child {
  code: string;
  title: string;
  description: string | null;
  mandatory: boolean;
  kind: ChildKind;
  questionDetailsJson: string | null;
}

const parseChildren = (questionDetailsJson: string): Child[] => {
  const group = GroupWithChildQuestionsV1.fromDTO(
    JSON.parse(questionDetailsJson),
  );
  return group.childQuestions.map((source) => {
    const childQuestion = source.childQuestion;
    let kind: ChildKind;
    let childQuestionDetailsJson: string | null = null;
    if (
      childQuestion instanceof
      ChildQuestionElementV1Enums.ChildQuestion.SingleChoice
    ) {
      kind = "single";
      childQuestionDetailsJson = JSON.stringify(
        childQuestion.singleChoiceQuestionAttributes.toDTO(),
      );
    } else if (
      childQuestion instanceof
      ChildQuestionElementV1Enums.ChildQuestion.MultiChoice
    ) {
      kind = "multi";
      childQuestionDetailsJson = JSON.stringify(
        childQuestion.multiChoiceQuestionAttributes.toDTO(),
      );
    } else if (
      childQuestion instanceof
      ChildQuestionElementV1Enums.ChildQuestion.OpenEnded
    ) {
      kind = "open";
      childQuestionDetailsJson = JSON.stringify(
        childQuestion.openEndedQuestionAttributes.toDTO(),
      );
    } else if (
      childQuestion instanceof ChildQuestionElementV1Enums.ChildQuestion.Ranking
    ) {
      kind = "ranking";
      childQuestionDetailsJson = JSON.stringify(
        childQuestion.rankingQuestionAttributes.toDTO(),
      );
    } else if (
      childQuestion instanceof ChildQuestionElementV1Enums.ChildQuestion.Grid
    ) {
      kind = "grid";
      childQuestionDetailsJson = JSON.stringify(
        childQuestion.gridQuestionAttributes.toDTO(),
      );
    } else {
      kind = "message";
    }
    return {
      code: source.childQuestionCode.code,
      title: source.question?.text ?? "",
      description: source.questionDescription?.text ?? null,
      mandatory: source.isMandatory,
      kind,
      questionDetailsJson: childQuestionDetailsJson,
    };
  });
};

const answerJsonFor = (entry: GroupAnswerElementV1): string => {
  const answer = entry.answer;
  if (answer instanceof GroupAnswerElementV1Enums.Answer.SingleChoice) {
    return JSON.stringify(answer.singleChoiceAnswerAttributes.toDTO());
  }
  if (answer instanceof GroupAnswerElementV1Enums.Answer.MultiChoice) {
    return JSON.stringify(answer.multiChoiceAnswerAttributes.toDTO());
  }
  if (answer instanceof GroupAnswerElementV1Enums.Answer.OpenEnded) {
    return JSON.stringify(answer.openEndedAnswerAttributes.toDTO());
  }
  if (answer instanceof GroupAnswerElementV1Enums.Answer.Ranking) {
    return JSON.stringify(answer.rankingQuestionAttributes.toDTO());
  }
  if (answer instanceof GroupAnswerElementV1Enums.Answer.Grid) {
    return JSON.stringify(answer.gridAnswerAttributes.toDTO());
  }
  return EMPTY_JSON;
};

const parseAnswers = (answerJson?: string | null): Record<string, string> => {
  if (!answerJson || answerJson === EMPTY_JSON) return {};
  try {
    const group = GroupAnswerV1.fromDTO(JSON.parse(answerJson));
    return Object.fromEntries(
      group.answer.map((entry) => [
        entry.questionCode.code,
        answerJsonFor(entry),
      ]),
    );
  } catch {
    return {};
  }
};

const hasAnswerValue = (child: Child, answerJson?: string): boolean => {
  if (!answerJson || answerJson === EMPTY_JSON) return false;
  try {
    const answer = JSON.parse(answerJson);
    if (child.kind === "multi") return answer.selectedChoices.length > 0;
    if (child.kind === "ranking") return answer.answers.length > 0;
    if (child.kind === "grid") return answer.answers.length > 0;
    if (child.kind === "open") {
      return answer.fieldAnswers.some(
        (field: { answer: string }) => field.answer.trim() !== "",
      );
    }
  } catch {
    return false;
  }
  return true;
};

const toGroupAnswerJson = (
  children: Child[],
  answers: Record<string, string>,
): string => {
  const entries = children.flatMap((child) => {
    const answerJson = answers[child.code];
    if (!hasAnswerValue(child, answerJson) || child.kind === "message") {
      return [];
    }
    try {
      const dto = JSON.parse(answerJson);
      const answer =
        child.kind === "single"
          ? new GroupAnswerElementV1Enums.Answer.SingleChoice(
              SingleChoiceAnswerV1.fromDTO(dto as never),
            )
          : child.kind === "multi"
            ? new GroupAnswerElementV1Enums.Answer.MultiChoice(
                MultiChoiceAnswerV1.fromDTO(dto as never),
              )
            : child.kind === "open"
              ? new GroupAnswerElementV1Enums.Answer.OpenEnded(
                  OpenEndedAnswerV1.fromDTO(dto as never),
                )
              : child.kind === "ranking"
                ? new GroupAnswerElementV1Enums.Answer.Ranking(
                    RankingAnswerV1.fromDTO(dto as never),
                  )
                : child.kind === "grid"
                  ? new GroupAnswerElementV1Enums.Answer.Grid(
                      GridAnswerV1.fromDTO(dto as never),
                    )
                  : undefined;
      if (!answer) return [];
      return [new GroupAnswerElementV1(new Code(child.code), answer)];
    } catch {
      return [];
    }
  });
  return JSON.stringify(new GroupAnswerV1(entries).toDTO());
};

const isCurrentAnswerValid = (
  child: Child,
  answerJson: string | undefined,
  isMandatory: boolean,
): boolean => {
  if (!hasAnswerValue(child, answerJson)) return !isMandatory;
  try {
    if (child.kind === "open") {
      const model = createOpenEndedQuestionV1Model(isMandatory);
      model.deserializeQuestionJSON(child.questionDetailsJson!);
      model.deserializeAnswerJSON(answerJson!);
      return !isMandatory || model.validateUserResponse === null;
    }
    if (child.kind === "ranking") {
      const model = createChatRankingQuestionModel(
        child.questionDetailsJson!,
        answerJson,
      );
      return model.validateUserResponse(isMandatory) === null;
    }
    if (child.kind === "grid") {
      const model = createGridQuestionV1Model();
      model.deserializeQuestionJSON(child.questionDetailsJson!);
      model.deserialiseAnswerJson(answerJson!);
      return validateGridAnswer(isMandatory, model) === null;
    }
  } catch {
    return false;
  }
  return true;
};

const CompactAnswer = ({
  child,
  answerJson,
  labels,
  compact = true,
}: {
  child: Child;
  answerJson?: string;
  labels: Pick<
    ChatGroupQuestionLabels,
    "skippedAnswerText" | "showMoreText" | "showLessText"
  >;
  compact?: boolean;
}): React.ReactElement => {
  const tokens = useFoundationColorTokens();
  const typography = useTypography();
  if (child.kind === "grid") {
    return (
      <ChatGridQuestionSummary
        questionDetailsJson={child.questionDetailsJson!}
        answerJson={answerJson}
        skippedAnswerText={labels.skippedAnswerText}
        compact={compact}
      />
    );
  }
  if (!answerJson) {
    return (
      <Typography
        sx={{
          ...typography.b2,
          ...(compact ? { color: tokens.labelSubtle } : {}),
        }}
      >
        {labels.skippedAnswerText}
      </Typography>
    );
  }
  const content =
    child.kind === "single" ? (
      <ChatSingleSelectAnswerText
        questionDetailsJson={child.questionDetailsJson!}
        answerJson={answerJson}
      />
    ) : child.kind === "multi" ? (
      <ChatMultiSelectAnswerList
        questionDetailsJson={child.questionDetailsJson!}
        answerJson={answerJson}
      />
    ) : child.kind === "open" ? (
      <ChatOpenEndedAnswer
        questionDetailsJson={child.questionDetailsJson!}
        answerJson={answerJson}
        showMoreText={labels.showMoreText}
        showLessText={labels.showLessText}
        skippedAnswerText={labels.skippedAnswerText}
      />
    ) : child.kind === "ranking" ? (
      <ChatRankingAnswerList
        questionDetailsJson={child.questionDetailsJson!}
        answerJson={answerJson}
      />
    ) : (
      <></>
    );
  return (
    <Box
      sx={
        compact
          ? {
              ...typography.b2,
              color: tokens.labelSubtle,
              "& *": { fontSize: "inherit", color: "inherit" },
            }
          : {}
      }
    >
      {content}
    </Box>
  );
};

export const ChatGroupQuestionActive = ({
  questionDetailsJson,
  completedAnswerJson,
  parentIsMandatory,
  initialChildQuestionCode,
  initialGridRowCode,
  labels,
  fontFamily,
  fontScale,
  areActionsDisabled = false,
  isEditing = false,
  onCancelEdit,
  onSubmit,
}: ChatGroupQuestionActiveProps): React.ReactElement => {
  const children = useMemo(
    () => parseChildren(questionDetailsJson),
    [questionDetailsJson],
  );
  const initialAnswers = useMemo(
    () => parseAnswers(completedAnswerJson),
    [completedAnswerJson],
  );
  const [answers, setAnswers] =
    useState<Record<string, string>>(initialAnswers);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      children.findIndex((child) => child.code === initialChildQuestionCode),
    ),
  );
  const activeStepRef = useRef<HTMLDivElement>(null);
  const previousActiveIndex = useRef(activeIndex);
  useLayoutEffect(() => {
    if (isEditing) return undefined;
    const frame = requestAnimationFrame(() => {
      revealNewChatStep(activeStepRef.current);
    });
    return (): void => cancelAnimationFrame(frame);
  }, []);
  useEffect(() => {
    setAnswers(initialAnswers);
    setActiveIndex(
      Math.max(
        0,
        children.findIndex((child) => child.code === initialChildQuestionCode),
      ),
    );
  }, [children, initialAnswers, initialChildQuestionCode]);
  useLayoutEffect(() => {
    const isMovingForward = activeIndex > previousActiveIndex.current;
    previousActiveIndex.current = activeIndex;
    if (!isMovingForward || isEditing) return undefined;

    const frame = requestAnimationFrame(() => {
      revealNewChatStep(activeStepRef.current);
    });
    return (): void => cancelAnimationFrame(frame);
  }, [activeIndex, isEditing]);
  const active = children[activeIndex];
  if (!active) return <></>;
  const needsAnswer =
    active.kind !== "message" && (parentIsMandatory || active.mandatory);
  const areGridAnswersValid = children.every(
    (child) =>
      child.kind !== "grid" ||
      isCurrentAnswerValid(
        child,
        answers[child.code],
        parentIsMandatory || child.mandatory,
      ),
  );
  const isBlocked =
    areActionsDisabled ||
    !isCurrentAnswerValid(active, answers[active.code], needsAnswer) ||
    ((isEditing || activeIndex === children.length - 1) &&
      !areGridAnswersValid);
  const setAnswer = useCallback(
    (answerJson: string | null): void => {
      setAnswers((current) => {
        if (answerJson) {
          return current[active.code] === answerJson
            ? current
            : { ...current, [active.code]: answerJson };
        }
        if (!(active.code in current)) return current;
        return Object.fromEntries(
          Object.entries(current).filter(([code]) => code !== active.code),
        );
      });
    },
    [active.code],
  );
  const submitGridAnswer = (answerJson: string): void => {
    const updatedAnswers = { ...answers };
    if (answerJson === EMPTY_JSON) {
      delete updatedAnswers[active.code];
    } else {
      updatedAnswers[active.code] = answerJson;
    }
    setAnswers(updatedAnswers);
    if (isEditing || activeIndex === children.length - 1) {
      onSubmit(toGroupAnswerJson(children, updatedAnswers));
    } else {
      setActiveIndex(activeIndex + 1);
    }
  };
  const body =
    active.kind === "single" ? (
      <ChatSingleSelectQuestionBody
        key={active.code}
        questionDetailsJson={active.questionDetailsJson!}
        draftAnswerJson={answers[active.code]}
        fontFamily={fontFamily}
        fontScale={fontScale}
        dropdownPlaceholder={labels.dropdownPlaceholder}
        onAnswerChange={setAnswer}
      />
    ) : active.kind === "multi" ? (
      <ChatMultiSelectQuestionBody
        key={active.code}
        questionDetailsJson={active.questionDetailsJson!}
        draftAnswerJson={answers[active.code]}
        fontFamily={fontFamily}
        fontScale={fontScale}
        maxSelectionText={labels.multiSelectMaxSelectionText}
        isMandatory={needsAnswer}
        onAnswerChange={setAnswer}
      />
    ) : active.kind === "open" ? (
      <ChatOpenEndedQuestionBody
        key={active.code}
        questionDetailsJson={active.questionDetailsJson!}
        draftAnswerJson={answers[active.code]}
        fontFamily={fontFamily}
        fontScale={fontScale}
        isMandatory={needsAnswer}
        onAnswerChange={setAnswer}
      />
    ) : active.kind === "ranking" ? (
      <ChatRankingQuestionBody
        key={active.code}
        questionDetailsJson={active.questionDetailsJson!}
        draftAnswerJson={answers[active.code]}
        fontFamily={fontFamily}
        fontScale={fontScale}
        areAllOptionsMandatoryText={labels.rankingAllOptionsMandatoryText}
        maxSelectionText={labels.multiSelectMaxSelectionText}
        isMandatory={needsAnswer}
        onAnswerChange={setAnswer}
        onResetReady={ignoreRankingReset}
      />
    ) : active.kind === "grid" ? (
      <ChatGridQuestionActive
        key={active.code}
        questionDetailsJson={active.questionDetailsJson!}
        completedAnswerJson={answers[active.code]}
        isMandatory={needsAnswer}
        initialRowCode={initialGridRowCode}
        labels={{
          ...labels,
          editAnswersText: labels.editAnswerText,
        }}
        fontFamily={fontFamily}
        fontScale={fontScale}
        areActionsDisabled={areActionsDisabled}
        isEditing={isEditing}
        onCancelEdit={(): void => onCancelEdit?.()}
        onDraftChange={setAnswer}
        onSubmit={submitGridAnswer}
      />
    ) : (
      <></>
    );
  return (
    // GroupActiveLayout is declared below the active controller to keep the
    // stateful controller and its stateless layout readable as separate units.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <GroupActiveLayout
      activeStepRef={activeStepRef}
      childQuestions={children}
      activeIndex={activeIndex}
      active={active}
      answers={answers}
      labels={labels}
      body={body}
      areActionsDisabled={areActionsDisabled}
      isBlocked={isBlocked}
      isEditing={isEditing}
      gridActive={active.kind === "grid"}
      onCancelEdit={onCancelEdit}
      onSubmit={(): void => onSubmit(toGroupAnswerJson(children, answers))}
      onPrevious={(): void => setActiveIndex((index) => index - 1)}
      onNext={(): void =>
        activeIndex === children.length - 1
          ? onSubmit(toGroupAnswerJson(children, answers))
          : setActiveIndex((index) => index + 1)
      }
    />
  );
};

function GroupActiveLayout({
  activeStepRef,
  childQuestions,
  activeIndex,
  active,
  answers,
  labels,
  body,
  areActionsDisabled,
  isBlocked,
  isEditing,
  gridActive,
  onCancelEdit,
  onPrevious,
  onNext,
  onSubmit,
}: {
  activeStepRef: React.RefObject<HTMLDivElement>;
  childQuestions: Child[];
  activeIndex: number;
  active: Child;
  answers: Record<string, string>;
  labels: ChatGroupQuestionLabels;
  body: React.ReactNode;
  areActionsDisabled: boolean;
  isBlocked: boolean;
  isEditing: boolean;
  gridActive: boolean;
  onCancelEdit?: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
}): React.ReactElement {
  const tokens = useFoundationColorTokens();
  const spacing = useSpacing();
  const typography = useTypography();
  return (
    <Stack gap={spacing.spaceSM}>
      <Stack gap={spacing.spaceSM}>
        {childQuestions
          .slice(0, activeIndex)
          .filter((child) => child.kind !== "message")
          .map((child) => (
            <Stack key={child.code} gap={spacing.spaceXXS}>
              <Typography
                sx={{
                  ...typography.b2,
                  fontWeight: 600,
                  color: tokens.labelSubtle,
                }}
              >{`${childQuestions.indexOf(child) + 1}/${childQuestions.length} ${child.title}`}</Typography>
              <Box
                padding={spacing.spaceSM}
                sx={{
                  backgroundColor: tokens.backgroundSubtle,
                  borderRadius: "14px",
                }}
              >
                <CompactAnswer
                  child={child}
                  answerJson={answers[child.code]}
                  labels={labels}
                />
              </Box>
            </Stack>
          ))}
      </Stack>
      <Box ref={activeStepRef}>
        <Typography
          sx={{ ...typography.b2, fontWeight: 600, color: tokens.label }}
        >{`${activeIndex + 1}/${childQuestions.length} ${active.title}`}</Typography>
      </Box>
      {active.description && (
        <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
          {active.description}
        </Typography>
      )}
      {active.kind !== "message" && (
        <Box
          padding={spacing.spaceSM}
          sx={{
            backgroundColor: tokens.backgroundSubtle,
            borderRadius: "14px",
            "--chat-group-option-border-color": isBrowserLightMode()
              ? tokens.border
              : tokens.borderHighEmphasis,
          }}
        >
          {body}
        </Box>
      )}
      <Stack
        data-chat-item-actions
        direction="row"
        justifyContent="flex-end"
        gap={spacing.spaceXS}
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          backgroundColor: tokens.backgroundElevatedLevel1,
          paddingBlock: "12px",
        }}
      >
        {gridActive ? (
          !isEditing && activeIndex > 0 ? (
            <Button
              name="previousGroupQuestion"
              variant="outlined-neutral"
              label={labels.previousText}
              size="medium"
              disabled={areActionsDisabled}
              onClick={onPrevious}
            />
          ) : null
        ) : isEditing ? (
          <>
            <Button
              name="cancelEditChatSurveyAnswer"
              variant="outlined-neutral"
              label={labels.cancelEditAnswerText}
              size="medium"
              disabled={areActionsDisabled}
              onClick={(): void => onCancelEdit?.()}
            />
            <Button
              name="sendChatSurveyResponse"
              variant="filled"
              label={labels.sendResponseText}
              size="medium"
              disabled={isBlocked}
              onClick={onSubmit}
            />
          </>
        ) : (
          <>
            <Button
              name="previousGroupQuestion"
              variant="outlined-neutral"
              label={labels.previousText}
              size="medium"
              disabled={areActionsDisabled || activeIndex === 0}
              onClick={onPrevious}
            />
            <Button
              name="nextGroupQuestion"
              variant="filled"
              size="medium"
              disabled={isBlocked}
              onClick={onNext}
              label={
                activeIndex === childQuestions.length - 1
                  ? labels.sendResponseText
                  : labels.nextQuestionText
              }
            />
          </>
        )}
      </Stack>
    </Stack>
  );
}

export const ChatGroupQuestionCompleted = ({
  questionDetailsJson,
  completedAnswerJson,
  labels,
  areActionsDisabled = false,
  showEditActions = true,
  onEditChild,
}: ChatGroupQuestionCompletedProps): React.ReactElement => {
  const tokens = useFoundationColorTokens();
  const spacing = useSpacing();
  const typography = useTypography();
  const { t } = useTranslation();
  const children = useMemo(
    () => parseChildren(questionDetailsJson),
    [questionDetailsJson],
  );
  const answers = useMemo(
    () => parseAnswers(completedAnswerJson),
    [completedAnswerJson],
  );
  return (
    <Stack gap={spacing.spaceMD}>
      {children
        .filter((child) => child.kind !== "message")
        .map((child) => (
          <Stack key={child.code} gap={spacing.spaceXS}>
            <ChatItemContainer
              alignment="outgoing"
              ariaLabel={t("surveys.chatInterface.groupSurveyAnswerAriaLabel")}
            >
              <Stack gap="10px">
                <Typography>{`${children.indexOf(child) + 1}/${children.length} ${child.title}`}</Typography>
                <Box
                  sx={{ borderTop: "1px solid currentColor", opacity: 0.35 }}
                />
                {child.kind === "grid" ? (
                  <ChatGridQuestionSummary
                    questionDetailsJson={child.questionDetailsJson!}
                    answerJson={answers[child.code]}
                    skippedAnswerText={labels.skippedAnswerText}
                    editAnswerText={
                      showEditActions ? labels.editAnswerText : undefined
                    }
                    areActionsDisabled={areActionsDisabled}
                    onEditRow={
                      showEditActions
                        ? (rowCode): void => onEditChild(child.code, rowCode)
                        : undefined
                    }
                  />
                ) : (
                  <CompactAnswer
                    child={child}
                    answerJson={answers[child.code]}
                    labels={labels}
                    compact={false}
                  />
                )}
              </Stack>
            </ChatItemContainer>
            {showEditActions && child.kind !== "grid" && (
              <Box
                component="button"
                type="button"
                disabled={areActionsDisabled}
                onClick={(): void => onEditChild(child.code)}
                sx={{
                  ...typography.b2,
                  alignSelf: "flex-end",
                  border: 0,
                  background: "transparent",
                  color: tokens.label,
                  cursor: areActionsDisabled ? "default" : "pointer",
                  fontWeight: 600,
                  padding: `${spacing.spaceXS} ${spacing.spaceSM}`,
                  "&:hover": {
                    background: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {labels.editAnswerText}
              </Box>
            )}
          </Stack>
        ))}
    </Stack>
  );
};
